import { formatHM, formatHMS, formatMoney } from '../lib/format'
import type { Kpi, Task } from '../types'

interface Props {
  kpi?: Kpi
  loading: boolean
  /** Идущая задача — оживляет плитку таймера справа. */
  running?: Task | null
  runningSeconds?: number
  onStopRunning?: (finish: boolean) => void
  /** Клик по плитке «без оценки» — включить фильтр на такие задачи. */
  onShowWithoutEstimate?: () => void
}

/** Верхний блок показателей журнала. */
export default function KpiHeader({
  kpi,
  loading,
  running,
  runningSeconds = 0,
  onStopRunning,
  onShowWithoutEstimate,
}: Props) {
  if (loading && !kpi) return <div className="empty">Считаем показатели…</div>
  if (!kpi) return null

  return (
    <div className="kpi-row">
      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">Отработано</div>
          <div className="value">{formatHM(kpi.tracked_seconds)}</div>
          <div className="sub">
            {kpi.by_day.length > 0 ? `за ${kpi.by_day.length} дн.` : 'нет отметок за период'}
          </div>
        </div>

        <div className="kpi">
          <div className="label">Задач</div>
          <div className="value">{kpi.tasks_count}</div>
          <div className="sub">
            завершено {kpi.tasks_done} · в работе {kpi.tasks_in_progress}
          </div>
        </div>

        <div className="kpi">
          <div className="label">Клиентов</div>
          <div className="value">{kpi.clients_count}</div>
          <div className="sub">
            {kpi.top_clients[0] ? `чаще всего: ${kpi.top_clients[0].client_name}` : '—'}
          </div>
        </div>

        <div className="kpi">
          <div className="label">Выплачено</div>
          <div className="value">{formatMoney(kpi.amount_paid)}</div>
          <div className="sub">специалист уже получил</div>
        </div>

        <div className={`kpi${kpi.tasks_without_rate > 0 ? ' alert' : ''}`}>
          <div className="label">К выплате</div>
          <div className="value">{formatMoney(kpi.amount_unpaid)}</div>
          <div className="sub">
            {kpi.tasks_without_rate > 0
              ? `${kpi.tasks_without_rate} задач без ставки исполнителя — они не считаются`
              : `начислено всего ${formatMoney(kpi.amount_total)}`}
          </div>
        </div>

        <div
          className={`kpi${kpi.without_estimate > 0 ? ' alert' : ''}${
            onShowWithoutEstimate ? ' clickable' : ''
          }`}
          onClick={kpi.without_estimate > 0 ? onShowWithoutEstimate : undefined}
          title={kpi.without_estimate > 0 ? 'Показать задачи без оценки' : undefined}
        >
          <div className="label">Без оценки</div>
          <div className="value">{kpi.without_estimate}</div>
          <div className="sub">
            {kpi.without_estimate > 0 ? 'нажмите, чтобы отфильтровать' : 'все задачи оценены'}
          </div>
        </div>
      </div>

      {/* Место под таймер занято всегда — плитка оживает на старте, но ничего
          не двигает: ни показатели слева, ни таблицу снизу. */}
      <div className={`kpi-timer-slot${running ? '' : ' idle'}`}>
        <div className={`kpi timer-kpi${running ? '' : ' idle'}`}>
          <div className="label">{running ? 'Идёт работа' : 'Таймер'}</div>
          <div className="value">{running ? formatHMS(runningSeconds) : '0:00:00'}</div>
          <div
            className="sub"
            title={running ? `${running.title} — ${running.client_name ?? ''}` : undefined}
          >
            {running ? `${running.client_name} · ${running.title}` : 'не запущен'}
          </div>
          {/* Скрытые кнопки в покое держат высоту плитки постоянной */}
          <div className="timer-kpi-actions" aria-hidden={!running}>
            <button
              className="btn small stop"
              tabIndex={running ? undefined : -1}
              onClick={() => onStopRunning?.(true)}
            >
              Стоп
            </button>
            <button
              className="btn small pause"
              tabIndex={running ? undefined : -1}
              onClick={() => onStopRunning?.(false)}
            >
              Пауза
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
